import React, { useCallback, useEffect, useRef, useState } from 'react';
import { loadMetaSdk, launchMetaSignup } from '../lib/metaEmbeddedSignup';
const t = (es) => es;

const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function WhatsAppConnect({ onConnected }) {
  const [config, setConfig] = useState(null);
  const [connection, setConnection] = useState(null);
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  const launch = useRef(null);
  const callback = useRef(onConnected);
  callback.current = onConnected;
  const clinicId = localStorage.getItem('clinicId');
  const token = localStorage.getItem('token');
  const endpoint = `${BASE}/api/clinic/${encodeURIComponent(clinicId || '')}/meta/embedded-signup`;
  const phoneIsValid = /^\+?[\d\s().-]+$/.test(phone.trim()) && /^\d{8,15}$/.test(phone.replace(/\D/g, ''));

  const request = useCallback(async (path, body) => {
    if (!clinicId || !token) throw new Error(t('Inicia sesión para conectar tu consultorio.', 'Sign in to connect your clinic.'));
    const res = await fetch(`${endpoint}/${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : t('No se pudo completar la conexión.', 'The request could not be completed.'));
    return data;
  }, [clinicId, token, endpoint]);

  useEffect(() => {
    active.current = true;
    (async () => {
      try {
        const [settings, status] = await Promise.all([request('config'), request('status')]);
        if (!active.current) return;
        setConfig(settings); setConnection(status);
        if (status.connected) { callback.current?.(status); return; }
        if (settings.available) await loadMetaSdk(settings.app_id, settings.graph_version);
      } catch (e) { if (active.current) setError(e.message); }
    })();
    return () => { active.current = false; launch.current?.cancel(); };
    // A clinic/session change creates a fresh component through its parent page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, token]);

  const refresh = async () => {
    try { setConnection(await request('status')); } catch (e) { setError(e.message); }
  };

  const prepare = async () => {
    if (!mode || !phoneIsValid) return;
    setError(''); setBusy(true);
    try {
      await loadMetaSdk(config.app_id, config.graph_version);
      const data = await request('start', { mode, phone_number: phone.trim() });
      if (active.current) setSession(data);
    } catch (e) { if (active.current) setError(e.message); }
    finally { if (active.current) setBusy(false); }
  };

  // Separate Continue click preserves the browser user gesture for FB.login;
  // no network await occurs before opening Meta's consent popup.
  const connect = async () => {
    setError(''); setBusy(true);
    const report = info => request('report', {
      state: session.state, event: info.event, current_step: info.data?.current_step,
      error_message: info.data?.error_message, error_id: info.data?.error_id,
    }).catch(() => {});
    launch.current = launchMetaSignup(window.FB, config.config_id,
      mode === 'coexistence' ? config.coexistence_extras : config.extras, report);
    try {
      const grant = await launch.current.promise;
      if (!active.current) return;
      await request('complete', { ...grant, state: session.state });
      const result = await request('status');
      if (active.current) { setConnection(result); callback.current?.(result); }
    } catch (e) { if (active.current) setError(e.message); }
    finally { if (active.current) { setBusy(false); setSession(null); } }
  };

  return <div className="space-y-3">
    {config?.test_only && <p className="rounded-md bg-amber-500/10 p-3 text-sm text-amber-500">
      Entorno de pruebas. Usa únicamente una cuenta de WhatsApp de prueba; no conectes el número de tus pacientes.
    </p>}
    {config?.available && !config.coexistence_available && <p className="text-sm text-gray-400">
      {config.coexistence_check?.reason || 'La conexión con WhatsApp Business en el celular no está disponible todavía.'}
    </p>}
    {connection?.connected ? <div role="status" className="space-y-3">
      <p className="text-emerald-400 font-medium">{t('WhatsApp vinculado', 'WhatsApp connected')} {connection.phone_number || ''}</p>
      {connection.mode === 'coexistence' && <p className="text-sm text-gray-400">
        Puedes seguir usando WhatsApp Business en el celular. Al recibir una respuesta enviada desde la app, Gina se pausa para ese contacto; puedes reanudarla desde Chats.
        {' '}La importación de contactos e historial es opcional y se guarda por separado; no activa respuestas de Gina.
      </p>}
      <p className="text-sm text-gray-400">{t('Envía un mensaje desde otro teléfono y comprueba que Gina responde antes de usarlo con pacientes.', 'Send a message from another phone and check that Gina replies before using it with patients.')}</p>
      {connection.evidence && <div className="rounded-md border border-gray-700 p-3 text-sm space-y-1" aria-label="Pruebas de conexión">
        <p>Recepción en AIMP: {connection.evidence.inbound ? 'Verificada' : 'Pendiente'}</p>
        <p>Entrega del mensaje de prueba: {({ not_tested: 'Sin probar', accepted: 'Aceptado por Meta; entrega pendiente', sent: 'Enviado; entrega pendiente', delivered: 'Entregado', read: 'Leído', failed: 'Falló' })[connection.evidence.test_delivery]}</p>
        {connection.mode === 'coexistence' && <p>Respuesta desde WhatsApp Business: {connection.evidence.app_reply ? 'Verificada' : 'Pendiente'}</p>}
        <p className="font-medium">{connection.evidence.operational ? 'Pruebas de conexión completadas' : 'Conexión configurada; faltan pruebas reales'}</p>
        <button type="button" onClick={refresh} className="text-green-400 underline">Comprobar pruebas</button>
      </div>}
      {connection.source === 'embedded_signup' || connection.verified_at ? <ConnectedTools request={request} refresh={refresh} connection={connection} /> : null}
    </div> : <>
      <p className="text-sm text-gray-400">Elige cómo usas el número antes de abrir Meta. No necesitas copiar claves.</p>
      {config?.available && <label className="block text-sm text-gray-300">
        Cómo usarás tu número
        <select value={mode} disabled={busy || !!session} onChange={e => setMode(e.target.value)}
          className="mt-1 block w-full rounded-md bg-gray-800 p-2 text-white">
          <option value="" disabled>Selecciona una opción</option>
          <option value="coexistence" disabled={!config.coexistence_available}>Conservar WhatsApp Business en mi celular</option>
          <option value="cloud_api">Conectar un número que no uso en la app del celular</option>
        </select>
      </label>}
      {mode === 'coexistence' && <p className="text-sm text-gray-300">Para un número que ya usas en la app WhatsApp Business. Conserva esa cuenta; no la elimines para conectarla a AIMP.</p>}
      {mode === 'cloud_api' && <p className="text-sm text-gray-300">Si el número ya está en WhatsApp Business en tu celular, elige la opción anterior para conservarlo.</p>}
      {config?.available && <label className="block text-sm text-gray-300">
        WhatsApp que conectarás
        <input type="tel" value={phone} disabled={busy || !!session} onChange={e => setPhone(e.target.value)}
          placeholder="+52 55 1234 5678" maxLength={25} className="mt-1 block w-full rounded-md bg-gray-800 p-2 text-white" />
        <span className="text-xs text-gray-400">Incluye el código de país. Puede ser distinto al teléfono de contacto de tu clínica.</span>
      </label>}
      {config?.available ? <button type="button" disabled={busy || !mode || !phoneIsValid} onClick={session ? connect : prepare}
        className="rounded-md bg-green-600 px-4 py-2 text-white disabled:opacity-50">
        {busy ? t('Conectando…', 'Connecting…') : session ? t('Continuar con Meta', 'Continue with Meta') : mode === 'coexistence' ? 'Conectar WhatsApp Business' : mode === 'cloud_api' ? 'Conectar Cloud API' : 'Elige cómo usas tu número'}
      </button> : <p className="text-sm text-gray-400">{config ? t('La conexión automática aún no está habilitada. Contacta a soporte para completar el alta.', 'Automatic connection is not enabled yet. Contact support.') : t('Comprobando conexión…', 'Checking connection…')}</p>}
      {session && !busy && <button type="button" onClick={() => setSession(null)} className="ml-3 text-sm text-gray-300 underline">Cambiar número u opción</button>}
    </>}
    {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
  </div>;
}

// Shown once the clinic is linked: reads the WABA's templates (whatsapp_business_management)
// and sends a message from the clinic's number (whatsapp_business_messaging).
function ConnectedTools({ request, refresh, connection }) {
  const [templates, setTemplates] = useState(null);
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const loadTemplates = useCallback(async () => {
    setError('');
    try { setTemplates((await request('templates')).templates); } catch (e) { setError(e.message); }
  }, [request]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const createSample = async () => {
    setBusy(true); setNotice(''); setError('');
    try {
      await request('templates/sample', {});
      setNotice(t('Plantilla creada. Meta la revisará antes de aprobarla.', 'Template created. Meta reviews it before approval.'));
      await loadTemplates();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true); setNotice(''); setError('');
    try {
      await request('test-message', { to, ...(text.trim() ? { text: text.trim() } : {}) });
      await refresh();
      setNotice(t('Meta aceptó el mensaje. La entrega todavía debe comprobarse en el teléfono.', 'Message sent. Check WhatsApp on that phone.'));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const sync = async kind => {
    setBusy(true); setError(''); setNotice('');
    try {
      await request('sync', { kind });
      setNotice('Meta aceptó la solicitud. La recepción de los datos sigue pendiente.');
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return <div className="space-y-4 border-t border-gray-700 pt-4" data-testid="whatsapp-connected-tools">
    {connection.mode === 'coexistence' && <div className="space-y-2">
      <p className="text-sm text-gray-400">Opcional: importar a AIMP los contactos o el historial que autorices compartir en Meta. Se solicita una sola vez tras el alta y no envía mensajes.</p>
      {['smb_app_state_sync', 'history'].map(kind => <button key={kind} type="button"
        disabled={busy || !!connection.sync?.[kind]?.requested} onClick={() => sync(kind)}
        className="mr-2 rounded-md border border-gray-600 px-3 py-2 text-sm text-gray-300 disabled:opacity-50">
        {kind === 'history' ? 'Importar historial' : 'Importar contactos'} · {connection.sync?.[kind]?.received ? 'Recibido' : connection.sync?.[kind]?.requested ? 'Solicitado' : 'Sin solicitar'}
      </button>)}
    </div>}
    <div>
      <p className="font-medium text-white">{t('Plantillas de mensajes', 'Message templates')}</p>
      <p className="text-xs text-gray-400 mb-2">{t('Plantillas aprobadas por Meta en tu cuenta de WhatsApp Business.', 'Meta-approved templates in your WhatsApp Business account.')}</p>
      {templates === null ? <p className="text-sm text-gray-400">{t('Cargando…', 'Loading…')}</p>
        : templates.length === 0 ? <p className="text-sm text-gray-400">{t('Sin plantillas todavía.', 'No templates yet.')}</p>
        : <ul className="text-sm text-gray-300 space-y-1">{templates.map(x =>
            <li key={x.name + x.language}>{x.name} · {x.language} · <span className="text-emerald-400">{x.status}</span></li>)}</ul>}
      <button type="button" disabled={busy} onClick={createSample}
        className="mt-2 rounded-md border border-green-600 px-3 py-1 text-sm text-green-400 disabled:opacity-50" data-testid="create-sample-template">
        {t('Crear plantilla de ejemplo', 'Create sample template')}
      </button>
    </div>
    <div className="space-y-2">
      <p className="font-medium text-white">{t('Enviar un mensaje', 'Send a message')}</p>
      <input value={to} onChange={e => setTo(e.target.value)} inputMode="tel"
        placeholder={t('Número con código de país, ej. 5215512345678', 'Number with country code, e.g. 15551234567')}
        className="w-full rounded-md bg-gray-800 px-3 py-2 text-white" data-testid="test-message-to" />
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
        placeholder={t('Mensaje (vacío = plantilla hello_world)', 'Message (empty = hello_world template)')}
        className="w-full rounded-md bg-gray-800 px-3 py-2 text-white" />
      <button type="button" disabled={busy || to.replace(/\D/g, '').length < 8} onClick={send}
        className="rounded-md bg-green-600 px-4 py-2 text-white disabled:opacity-50" data-testid="test-message-send">
        {busy ? t('Enviando…', 'Sending…') : t('Enviar mensaje', 'Send message')}
      </button>
      {notice && <p className="text-sm text-emerald-400" role="status">{notice}</p>}
      {error && <p className="text-sm text-red-400" role="alert">{error}</p>}
    </div>
  </div>;
}
